import { PLATFORM_LEGAL_PATHS } from '@garage/shared';
import { useQuery } from '@tanstack/react-query';
import { api } from '../shared/api.js';

declare const __PRODUCT_NAME_FALLBACK__: string | undefined;
declare const __PRODUCT_NAME_PRO_FALLBACK__: string | undefined;

/**
 * Repli si l'API ne répond pas : `PRODUCT_NAME` de la configuration, injecté au build par Vite
 * (`vite.config.ts`). Jamais un nom écrit dans le code ; absent (tests, build sans la variable) : vide.
 */
export const PRODUCT_NAME_FALLBACK: string =
  typeof __PRODUCT_NAME_FALLBACK__ === 'string' ? __PRODUCT_NAME_FALLBACK__.trim() : '';

/** Repli de la face garage (`PRODUCT_NAME_PRO`, ou « PRODUCT_NAME Pro »), injecté au build ; sinon vide. */
export const PRODUCT_NAME_PRO_FALLBACK: string =
  typeof __PRODUCT_NAME_PRO_FALLBACK__ === 'string' ? __PRODUCT_NAME_PRO_FALLBACK__.trim() : '';

/**
 * Pages légales de la plateforme (vague 7, `public/LegalPages.tsx`) : publiques, sans marque de garage.
 * Liens simples (`<a>`) : utilisables hors du routeur (pied du portail, compte).
 */
export const PLATFORM_LEGAL_LINKS = [
  { href: PLATFORM_LEGAL_PATHS.privacy, label: 'Confidentialité' },
  { href: PLATFORM_LEGAL_PATHS.legalNotice, label: 'Mentions légales' },
] as const;

export const PLATFORM_QUERY_KEY = ['public.platform'] as const;

/**
 * Décision du 04/10/2026 (« une marque, deux faces ») : `owner` = face propriétaire (portail client,
 * `productName`) ; `pro` = face garage (connexion de l'équipe, coque garage, console, démonstration,
 * `productNamePro`).
 */
export type ProductFace = 'owner' | 'pro';

/**
 * Nom du produit (`public.platform`, sans session) pour une face. Pendant le chargement : `null` (rien
 * n'est affiché, pour ne jamais montrer le repli si l'API répond) ; en cas d'échec : le repli de
 * configuration (éventuellement vide).
 */
export function useProductName(face: ProductFace = 'owner'): string | null {
  const q = useQuery({
    queryKey: PLATFORM_QUERY_KEY,
    queryFn: () => api('public.platform'),
    staleTime: Infinity,
    gcTime: Infinity,
    retry: 1,
  });
  if (q.data) return face === 'pro' ? q.data.productNamePro : q.data.productName;
  if (q.isError) return face === 'pro' ? PRODUCT_NAME_PRO_FALLBACK : PRODUCT_NAME_FALLBACK;
  return null;
}

/**
 * Suffixe visible de la face garage à côté du logo : ce que le nom Pro ajoute au nom simple
 * (`PRODUCT_NAME_PRO` = « <PRODUCT_NAME> Pro » → « Pro »). Vide si les deux noms ne se prolongent pas.
 */
export function proSuffix(owner: string | null, pro: string | null): string {
  if (!owner || !pro) return '';
  const o = owner.trim();
  const p = pro.trim();
  return p.toLocaleLowerCase('fr').startsWith(`${o.toLocaleLowerCase('fr')} `) ? p.slice(o.length).trim() : '';
}

/** Nom Pro (annoncé) et suffixe visible du logo, pour les écrans de la face garage. */
export function usePlatformIdentity(): { name: string | null; suffix: string } {
  const owner = useProductName('owner');
  const pro = useProductName('pro');
  return { name: pro, suffix: proSuffix(owner, pro) };
}
